import { useEffect, useMemo, useState } from 'react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import useAuth from 'src/hooks/useAuth';
import { useSelector, useDispatch } from 'src/redux/store';
import Scrollbar from 'src/components/Scrollbar';
import Iconify from 'src/components/Iconify';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import getDateLocale from 'src/utils/getDateLocale';
import {
  setSelectedPost,
  replyToComment,
  toggleHideComment,
  removeSocialComment
} from 'src/redux/slices/socialComments';
import { Button } from 'src/@/components/ui/button';
import { cn } from 'src/@/lib/utils';

const PLATFORM_ICON = {
  facebook: 'ic:baseline-facebook',
  instagram: 'mdi:instagram'
};

const PLATFORM_BADGE_BG = {
  facebook: 'bg-[#1877F2]',
  instagram: 'bg-gradient-to-tr from-amber-400 via-pink-500 to-violet-600'
};

const AVATAR_COLORS = [
  'bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600',
  'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function authorName(comment, t) {
  if (comment.isMe) return t('comments.you');
  return comment.from?.name || comment.from?.username || '—';
}

function relativeTime(date) {
  try {
    return formatDistanceToNowStrict(new Date(date), { locale: getDateLocale() });
  } catch (error) {
    return '';
  }
}

// Comentario no estilo Facebook: avatar + bolha cinza (nome em negrito + texto) e
// linha de acoes textuais abaixo (tempo · Responder · Ocultar · Excluir).
function CommentItem({ comment, isReply, isReplying, onReply, onToggleHide, onDelete, actioningCommentId, t }) {
  const nome = authorName(comment, t);
  const isActioning = actioningCommentId === comment.comment_id;

  return (
    <div className={cn('flex items-start gap-2', isReply && 'ml-11')}>
      <div className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold text-white',
        comment.isMe ? 'bg-violet-500' : getAvatarColor(nome)
      )}>
        {nome.substring(0, 1).toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <div className={cn(
          'inline-block max-w-full rounded-2xl px-3 py-1.5 transition-all',
          comment.isMe ? 'bg-violet-50 dark:bg-violet-500/10' : 'bg-gray-100 dark:bg-neutral-800',
          isReplying && 'ring-2 ring-[#7C3AED] ring-offset-1 dark:ring-offset-neutral-900',
          comment.is_hidden && 'opacity-60'
        )}>
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-semibold text-gray-900 dark:text-white">{nome}</span>
            {comment.is_hidden && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-amber-600 dark:text-amber-400">
                <Iconify icon="eva:eye-off-outline" width={9} />
                {t('comments.hidden')}
              </span>
            )}
          </div>
          <p className="text-[13px] text-gray-800 dark:text-neutral-200 whitespace-pre-wrap break-words">
            {comment.text}
          </p>
        </div>

        {/* Acoes textuais, como no Facebook: "6 sem  Curtir  Responder  Ocultar" */}
        <div className="flex items-center gap-3 mt-0.5 ml-3 text-[11px] text-gray-500 dark:text-neutral-400">
          {comment.comment_created_time && <span>{relativeTime(comment.comment_created_time)}</span>}
          {!isReply && (
            <button
              onClick={() => onReply(isReplying ? null : comment)}
              className={cn('font-semibold hover:underline', isReplying && 'text-[#7C3AED]')}
            >
              {t('comments.reply')}
            </button>
          )}
          <button onClick={() => onToggleHide(comment)} className="font-semibold hover:underline">
            {comment.is_hidden ? t('comments.unhide') : t('comments.hide')}
          </button>
          <button onClick={() => onDelete(comment)} className="font-semibold hover:underline hover:text-red-600">
            {t('comments.delete')}
          </button>
          {isActioning && <Iconify icon="svg-spinners:ring-resize" width={11} />}
        </div>
      </div>
    </div>
  );
}

export default function CommentThread() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { t } = useTranslation('chat');
  const { enqueueSnackbar } = useSnackbar();

  const { selectedPost, commentsByPost, loadingComments, sendingReply, actioningCommentId } = useSelector(
    (state) => state.socialComments
  );

  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const comments = commentsByPost[selectedPost?.post_id] || [];

  // O componente nao desmonta ao trocar de post, entao a resposta em andamento
  // precisa ser descartada — senao ela apontaria para um comentario de outro post.
  useEffect(() => {
    setReplyingTo(null);
    setReplyText('');
  }, [selectedPost?.post_id]);

  const { roots, repliesByParent } = useMemo(() => {
    const rootsList = [];
    const replies = {};
    comments.forEach((c) => {
      if (c.parent_id) {
        if (!replies[c.parent_id]) replies[c.parent_id] = [];
        replies[c.parent_id].push(c);
      } else {
        rootsList.push(c);
      }
    });
    return { roots: rootsList, repliesByParent: replies };
  }, [comments]);

  if (!selectedPost) return null;

  let postDate = '';
  try {
    if (selectedPost.post_created_time) {
      postDate = format(new Date(selectedPost.post_created_time), 'dd MMM yyyy', { locale: getDateLocale() });
    }
  } catch (error) {
    postDate = '';
  }

  const basePayload = {
    cliente_id: user.clienteId,
    canal_id: selectedPost.canal_id,
    platform: selectedPost.platform,
    post_id: selectedPost.post_id
  };

  // Sem replyingTo: comentario raiz no proprio post ("Comente como sua página").
  const handleSend = async () => {
    if (!replyText.trim()) return;
    const payload = { ...basePayload, message: replyText.trim() };
    if (replyingTo) payload.comment_id = replyingTo.comment_id;

    const result = await dispatch(replyToComment(payload));
    if (result.success) {
      enqueueSnackbar(t('comments.replySent'), { variant: 'success' });
      setReplyText('');
      setReplyingTo(null);
    } else {
      enqueueSnackbar(result.message || t('comments.actionError'), { variant: 'error' });
    }
  };

  const handleToggleHide = async (comment) => {
    const result = await dispatch(toggleHideComment({ ...basePayload, comment_id: comment.comment_id, hidden: !comment.is_hidden }));
    if (!result.success) enqueueSnackbar(result.message || t('comments.actionError'), { variant: 'error' });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoadingDelete(true);
      const result = await dispatch(removeSocialComment({ ...basePayload, comment_id: commentToDelete.comment_id }));
      if (result.success) {
        enqueueSnackbar(t('comments.deleted'), { variant: 'success' });
      } else {
        enqueueSnackbar(result.message || t('comments.actionError'), { variant: 'error' });
      }
    } finally {
      setLoadingDelete(false);
      setCommentToDelete(null);
    }
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50 dark:bg-neutral-950">
      {/* Barra superior: resumo do post + fechar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex-shrink-0">
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-100 dark:bg-neutral-800 flex items-center justify-center">
            {selectedPost.thumbnail_url ? (
              <img src={selectedPost.thumbnail_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <Iconify icon="solar:gallery-bold" width={16} className="text-gray-400" />
            )}
          </div>
          <span className={cn(
            'absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white dark:border-neutral-900',
            PLATFORM_BADGE_BG[selectedPost.platform]
          )}>
            <Iconify icon={PLATFORM_ICON[selectedPost.platform]} width={9} className="text-white" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">
            {selectedPost.message || t('comments.viewPost')}
          </p>
          <p className="text-[11px] text-gray-500 dark:text-neutral-400">
            {t('comments.commentsCount', { count: comments.length })}{postDate ? ` · ${postDate}` : ''}
          </p>
        </div>
        {selectedPost.permalink_url && (
          <a
            href={selectedPost.permalink_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-violet-600 hover:underline inline-flex items-center gap-1 flex-shrink-0"
          >
            <Iconify icon="eva:external-link-outline" width={12} />
            {t('comments.viewPost')}
          </a>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="w-8 h-8 rounded-lg flex-shrink-0"
          onClick={() => dispatch(setSelectedPost(null))}
        >
          <Iconify icon="eva:close-outline" width={18} />
        </Button>
      </div>

      {/* Duas colunas: comentarios a esquerda, preview do post a direita */}
      <div className="flex-1 flex min-h-0">
        {/* Coluna esquerda: thread de comentarios + composer */}
        <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-neutral-900 border-r border-gray-100 dark:border-neutral-800">
          <div className="flex-1 min-h-0">
            <Scrollbar sx={{ height: '100%' }}>
              <div className="px-5 py-4">
                <p className="text-[12px] font-semibold text-gray-500 dark:text-neutral-400 mb-4">
                  {t('comments.mostRecent')}
                </p>

                {loadingComments ? (
                  <div className="flex items-center justify-center py-16">
                    <Iconify icon="svg-spinners:ring-resize" width={22} className="text-violet-500" />
                  </div>
                ) : roots.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 bg-violet-50 dark:bg-violet-500/10">
                      <Iconify icon="eva:message-circle-outline" width={28} className="text-violet-600" />
                    </div>
                    <p className="text-[13px] font-medium text-gray-700 dark:text-neutral-300">{t('comments.emptyComments')}</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {roots.map((comment) => (
                      <div key={comment.comment_id} className="flex flex-col gap-2">
                        <CommentItem
                          comment={comment}
                          isReply={false}
                          isReplying={replyingTo?.comment_id === comment.comment_id}
                          onReply={setReplyingTo}
                          onToggleHide={handleToggleHide}
                          onDelete={setCommentToDelete}
                          actioningCommentId={actioningCommentId}
                          t={t}
                        />
                        {(repliesByParent[comment.comment_id] || []).map((reply) => (
                          <CommentItem
                            key={reply.comment_id}
                            comment={reply}
                            isReply
                            isReplying={replyingTo?.comment_id === reply.comment_id}
                            onReply={setReplyingTo}
                            onToggleHide={handleToggleHide}
                            onDelete={setCommentToDelete}
                            actioningCommentId={actioningCommentId}
                            t={t}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Scrollbar>
          </div>

          {/* Composer estilo "Comente como sua página" */}
          <div className="border-t border-gray-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-5 py-3 flex-shrink-0">
            {replyingTo && (
              <div className="mb-2">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 text-[11px] font-medium">
                  <Iconify icon="eva:corner-up-left-outline" width={11} />
                  {t('comments.replyingTo', { name: authorName(replyingTo, t) })}
                  <button onClick={() => setReplyingTo(null)} className="ml-1 hover:text-violet-900">
                    <Iconify icon="eva:close-outline" width={11} />
                  </button>
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                PLATFORM_BADGE_BG[selectedPost.platform]
              )}>
                <Iconify icon={PLATFORM_ICON[selectedPost.platform]} width={15} className="text-white" />
              </span>
              <input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={replyingTo
                  ? t('comments.replyPlaceholder')
                  : t('comments.commentPlaceholder')}
                className="flex-1 h-9 rounded-full border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 px-4 text-[13px] text-gray-900 dark:text-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 focus:outline-none"
              />
              <Button
                onClick={handleSend}
                disabled={!replyText.trim() || sendingReply}
                size="icon"
                className="w-9 h-9 rounded-full bg-[#7C3AED] hover:bg-[#6d28d9] text-white flex-shrink-0"
              >
                <Iconify icon={sendingReply ? 'svg-spinners:ring-resize' : 'eva:paper-plane-outline'} width={15} />
              </Button>
            </div>
          </div>
        </div>

        {/* Coluna direita: preview do post (escondida em telas pequenas) */}
        <div className="hidden lg:flex w-[42%] max-w-[460px] flex-col bg-gradient-to-b from-slate-50 to-slate-100 dark:from-neutral-950 dark:to-neutral-900">
          <Scrollbar sx={{ height: '100%' }}>
            <div className="px-5 py-5">
              <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
                {/* Cabecalho do post */}
                <div className="flex items-center gap-2.5 px-4 py-3">
                  <span className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0',
                    PLATFORM_BADGE_BG[selectedPost.platform]
                  )}>
                    <Iconify icon={PLATFORM_ICON[selectedPost.platform]} width={18} className="text-white" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-gray-900 dark:text-white">
                      {selectedPost.platform === 'facebook' ? 'Facebook' : 'Instagram'}
                    </p>
                    {postDate && <p className="text-[11px] text-gray-500 dark:text-neutral-400">{postDate}</p>}
                  </div>
                  {selectedPost.permalink_url && (
                    <a
                      href={selectedPost.permalink_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors flex-shrink-0"
                      title={t('comments.viewPost')}
                    >
                      <Iconify icon="eva:external-link-outline" width={15} />
                    </a>
                  )}
                </div>

                {/* Midia */}
                {(selectedPost.media_url || selectedPost.thumbnail_url) && (
                  <div className="bg-gray-950 flex items-center justify-center">
                    <img
                      src={selectedPost.media_url || selectedPost.thumbnail_url}
                      alt=""
                      className="w-full max-h-[420px] object-contain"
                    />
                  </div>
                )}

                {/* Legenda */}
                {selectedPost.message && (
                  <p className="px-4 py-3 text-[13px] text-gray-800 dark:text-neutral-200 whitespace-pre-wrap break-words">
                    {selectedPost.message}
                  </p>
                )}

                {/* Rodape com contagem de comentarios */}
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-t border-gray-100 dark:border-neutral-800 text-[11px] text-gray-500 dark:text-neutral-400">
                  <Iconify icon="eva:message-circle-outline" width={13} />
                  {t('comments.commentsCount', { count: comments.length })}
                </div>
              </div>
            </div>
          </Scrollbar>
        </div>
      </div>

      <ModalConfimacao
        open={!!commentToDelete}
        onClose={() => setCommentToDelete(null)}
        onConfirm={handleConfirmDelete}
        loadingFinalizar={loadingDelete}
        mensagem={t('comments.deleteConfirm')}
      />
    </div>
  );
}
